import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import './index.less';

import IbIcon from '../IbIcon';
import IbPopover from '../IbPopover';
import Avatar, { AvatarName } from '../Avatar';
import IbButton from '../IbButton';
import IbTypography from '../IbTypography';

import {
  DEFAULT_AVATAR_NAME,
  MAIN_CLASS_NAME,
  POPOVER_CONTENT_CLASS_NAME,
  POPOVER_GRID_CLASS_NAME,
  POPOVER_GRID_ITEM_CLASS_NAME,
} from './const';

export interface IAvatarPickerProps {
  avatarName?: string;
  onChange?: (avatarName: string) => void;
}

const AvatarPicker: React.FC<IAvatarPickerProps> = ({ avatarName = DEFAULT_AVATAR_NAME, onChange }) => {
  const { t } = useTranslation();
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState(avatarName);
  const availableAvatarNames = [
    'robot-01',
    'robot-02',
    'robot-03',
    'robot-04',
    'robot-05',
    'robot-06',
    'robot-07',
    'robot-08',
    'robot-09',
    'robot-10',
    'default',
  ];

  const onAvatarChange = () => {
    onChange?.(selectedAvatar);
    setPopoverVisible(false);
  };

  const onAvatarSelect = (avatarName: string) => setSelectedAvatar(avatarName);

  const renderAvatarCell = (avatarName: string, index: number) => {
    return (
      <div key={index} className={POPOVER_GRID_ITEM_CLASS_NAME} onClick={() => onAvatarSelect(avatarName)}>
        <Avatar key={avatarName} avatarName={avatarName as AvatarName} size={40} />
        {selectedAvatar === avatarName && <IbIcon iconName="check-one" size={20} />}
      </div>
    );
  };

  const renderPopoverContent = () => (
    <div className={POPOVER_CONTENT_CLASS_NAME}>
      <IbTypography.Paragraph strong type="secondary">
        {t('Avatar picker: Title')}
      </IbTypography.Paragraph>
      <div className={POPOVER_GRID_CLASS_NAME}>
        {availableAvatarNames.map((avatarName, index) => renderAvatarCell(avatarName, index))}
      </div>
      <IbButton type="fill" onClick={onAvatarChange}>
        {t('Avatar picker: Button: Done')}
      </IbButton>
    </div>
  );

  const onPopoverVisibleChange = (visible?: boolean) => {
    if (visible) return;
    setPopoverVisible(visible || false);
  };

  const onPopoverClick = () => {
    setSelectedAvatar(avatarName);
    setPopoverVisible(true);
  };

  return (
    <IbPopover
      content={renderPopoverContent()}
      placement="bottomLeft"
      trigger="click"
      visible={popoverVisible}
      onVisibleChange={onPopoverVisibleChange}
    >
      <div className={MAIN_CLASS_NAME} role="button" tabIndex={0} onClick={onPopoverClick}>
        <Avatar avatarName={avatarName as AvatarName} shape="square" size={48} />
        <IbIcon iconName="camera" />
      </div>
    </IbPopover>
  );
};

export default AvatarPicker;
